import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmailAPI, TokenManager } from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import './EmailVerifyPage.css';

function EmailVerifyPage() {
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const inputRefs = useRef([]);
    const { updateUser } = useAuth();

    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendMessage, setResendMessage] = useState('');

    const user = TokenManager.getUser();

    // iOS/Safari 자동재생 보정
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const tryPlay = () => v.play().catch(() => {});
        v.addEventListener('canplay', tryPlay, { once: true });
        return () => v.removeEventListener('canplay', tryPlay);
    }, []);

    // 첫 번째 입력 필드에 포커스
    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    // 재발송 쿨다운 타이머
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    // 6자리 완료 시 자동 검증
    useEffect(() => {
        const fullCode = code.join('');
        if (fullCode.length === 6) {
            handleVerify(fullCode);
        }
    }, [code]);

    // 인증 코드 검증
    const handleVerify = async (verificationCode) => {
        setIsLoading(true);
        setError('');

        try {
            const response = await EmailAPI.verify(verificationCode);

            // 사용자 정보 업데이트 (이메일 인증 완료 상태)
            if (response.user) {
                updateUser(response.user);
            }

            // 인증 성공 시 intro 페이지로 이동
            navigate('/intro');
        } catch (error) {
            setError(error.message || '인증 코드가 올바르지 않습니다.');
            // 에러 시 코드 초기화 및 첫 입력창에 포커스
            setCode(['', '', '', '', '', '']);
            if (inputRefs.current[0]) {
                inputRefs.current[0].focus();
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 입력 필드 변경 핸들러
    const handleChange = (index, value) => {
        // 숫자만 허용
        if (value && !/^\d$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError('');

        // 다음 입력 필드로 자동 포커스
        if (value && index < 5 && inputRefs.current[index + 1]) {
            inputRefs.current[index + 1].focus();
        }
    };

    // 키보드 이벤트 핸들러
    const handleKeyDown = (index, e) => {
        // 백스페이스: 이전 필드로 이동
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    // 붙여넣기 핸들러
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

        if (pastedData.length > 0) {
            const newCode = [...code];
            for (let i = 0; i < 6; i++) {
                newCode[i] = pastedData[i] || '';
            }
            setCode(newCode);

            // 마지막 입력된 필드 다음으로 포커스
            const focusIndex = Math.min(pastedData.length, 5);
            if (inputRefs.current[focusIndex]) {
                inputRefs.current[focusIndex].focus();
            }
        }
    };

    // 재발송 핸들러
    const handleResend = async () => {
        if (resendCooldown > 0) return;

        try {
            await EmailAPI.resend();
            setResendCooldown(60);
            setResendMessage('인증 메일이 재발송되었습니다.');
            setError('');

            // 3초 후 메시지 제거
            setTimeout(() => setResendMessage(''), 3000);
        } catch (error) {
            setError(error.message || '재발송 중 오류가 발생했습니다.');
        }
    };

    return (
        <div className="email-verify-container">
            <div className="bg-video">
                <video
                    ref={videoRef}
                    className="bg-video-el"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster={`${process.env.PUBLIC_URL}/images/login-poster.jpg`}
                >
                    <source src={`${process.env.PUBLIC_URL}/videos/login-bg.mp4`} type="video/mp4" />
                </video>
                <div className="bg-overlay" />
            </div>

            <div className="email-verify-box">
                <h1 className="logo">MAP Group CIS</h1>
                <h2 className="verify-title">이메일 인증</h2>

                <p className="verify-description">
                    <span className="email-address">{user?.email || '이메일'}</span>으로<br />
                    발송된 6자리 인증코드를 입력해주세요.
                </p>

                {error && <div className="error-message">{error}</div>}
                {resendMessage && <div className="success-message">{resendMessage}</div>}

                <div className="code-inputs">
                    {code.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            className={`code-input ${error ? 'input-error' : ''}`}
                            disabled={isLoading}
                        />
                    ))}
                </div>

                {isLoading && <div className="loading-text">인증 중...</div>}

                <div className="resend-section">
                    <span className="resend-text">인증 메일을 받지 못하셨나요?</span>
                    <button
                        type="button"
                        onClick={handleResend}
                        className="resend-button"
                        disabled={resendCooldown > 0}
                    >
                        {resendCooldown > 0 ? `${resendCooldown}초 후 재발송` : '재발송'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default EmailVerifyPage;
